import { useMarkPickedUpMutation } from '@/dealers/_gen/gql'
import { ApolloError } from '@apollo/client'
import Action from '@/gf/components/Action'
import CloseModalButton from '@/gf/components/CloseModalButton'
import DateTimeInput from '@/gf/components/inputs/DateTime'
import Modal from '@/gf/components/ModalNext'
import Field from '@/gf/components/next/forms/Field'
import TextInput from '@/gf/components/next/forms/TextInput'
import useMsgs from '@/gf/hooks/useMsgs'
import { ModalSize } from '@/types'
import { DateTime } from 'luxon'
import { FormEvent, useState } from 'react'

const MarkPickedUpModal = ({
  open,
  onClose,
  storeOrderId,
  onSuccess,
}: {
  open: boolean
  onClose: () => void
  storeOrderId: string
  onSuccess: () => void
}) => {
  const [_, msgs] = useMsgs()
  const [markPickedUp, { loading: performing }] = useMarkPickedUpMutation()
  // A counter clicks this after the fact, so the pickup time is asked for rather
  // than taken from the click. Now is the common answer, and the only bound that
  // matters is that it can't be in the future — the server clamps the other end,
  // refusing a receipt earlier than the quote's approval.
  const [pickedUpAt, setPickedUpAt] = useState<DateTime>(DateTime.now())
  const [collectedBy, setCollectedBy] = useState('')

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    e.stopPropagation()

    try {
      await markPickedUp({
        variables: {
          storeOrderId,
          receivedAt: pickedUpAt,
          collectedBy: collectedBy.trim() || null,
        },
      })
    } catch (error) {
      // The server's refusals are written to be read — "This order is not waiting
      // for pickup" is what a stale page gets — so show one when there is one.
      const refusal = error instanceof ApolloError ? error.graphQLErrors[0]?.message : undefined

      msgs.add(refusal || 'Error, please contact support.', 'negative')
      return
    }

    onClose()
    onSuccess()
  }

  return (
    <Modal open={open} onClose={onClose} size={ModalSize.SM}>
      <form onSubmit={submit} className="relative p-8 flex flex-col gap-y-6">
        <CloseModalButton onClick={onClose} className="absolute top-3 right-3" />

        <div className="space-y-2">
          <h2 className="font-medium text-2xl">Customer Picked Up</h2>

          <p className="text-lg">
            Record that your customer collected this order. It will show as received, and neither
            you nor your customer needs to do anything more with it.
          </p>
        </div>

        <Field label="When did your customer pick up the parts?">
          <DateTimeInput
            className="px-3 py-2 w-full"
            dateTime={pickedUpAt}
            updateDateTime={(dateTime) => dateTime && setPickedUpAt(dateTime)}
            max={DateTime.now()}
            required
          />
        </Field>

        <Field label="Who picked them up?" desc="Optional — the name goes on the order's timeline.">
          <TextInput
            value={collectedBy}
            setValue={setCollectedBy}
            placeholder="Name of the person at the counter"
          />
        </Field>

        <div className="flex gap-x-3">
          <Action.S onClick={onClose} className="w-1/2 font-medium">
            Cancel
          </Action.S>

          <Action.P type="submit" performing={performing} className="w-1/2 font-medium">
            Mark Picked Up
          </Action.P>
        </div>
      </form>
    </Modal>
  )
}

export default MarkPickedUpModal
